// Shown every time a community (Creator / Public Gallery) verse is about to be played: these
// verses are made by players, Anime Draft is a free non-profit fan project with no affiliation
// to any rights holder, and this is how a rights holder can get a verse taken down.
import { Button } from '@/components/ui/button';

export const TAKEDOWN_EMAIL = 'anime.draft.dev@gmail.com';

export function isCommunityVerse(verse: string | null | undefined): boolean {
  return Boolean(verse?.startsWith('Creator: '));
}

export function communityVerseName(verse: string | null | undefined): string {
  return (verse ?? '').replace(/^Creator: /, '');
}

export function takedownMailto(verseName: string) {
  return `mailto:${TAKEDOWN_EMAIL}?subject=${encodeURIComponent(`Takedown request: ${verseName || 'community verse'}`)}`;
}

export function CommunityVerseNotice({
  verseName,
  onContinue,
  onBack,
  backLabel = 'Go back',
}: {
  verseName: string;
  onContinue: () => void;
  onBack?: () => void;
  backLabel?: string;
}) {
  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="community-verse-notice-title"
    >
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="border-b border-border px-5 py-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">Community verse</p>
          <h2 id="community-verse-notice-title" className="mt-1 font-display text-lg font-extrabold leading-snug text-foreground">
            {verseName ? <>&ldquo;{verseName}&rdquo; was made by a player</> : 'This verse was made by a player'}
          </h2>
        </div>
        <div className="space-y-3 px-5 py-4 text-sm leading-relaxed text-muted-foreground">
          <p>
            Community verses are created and shared by players, not by Anime Draft. Public Gallery verses are
            reviewed before they go live to keep out low-effort and NSFW content, but that review does not mean
            Anime Draft or any rights holder endorses them.
          </p>
          <p>
            Anime Draft is a free, <span className="font-semibold text-foreground">non-profit fan project</span>. It is
            not affiliated with, sponsored by or endorsed by any studio, publisher or rights holder, including the
            owners of the characters in this verse.
          </p>
          <p>All characters, names and artwork belong to their respective owners. No copyright infringement is intended.</p>
          <div className="rounded-xl border border-border bg-secondary/40 px-3 py-2.5">
            Own the rights to something in this verse and want it removed? Email{' '}
            <a href={takedownMailto(verseName)} className="font-semibold text-primary underline underline-offset-2">
              {TAKEDOWN_EMAIL}
            </a>{' '}
            and it will be taken down promptly.
          </div>
        </div>
        <div className="flex flex-col-reverse gap-2 px-5 pb-5 sm:flex-row sm:justify-end">
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              {backLabel}
            </Button>
          )}
          <Button onClick={onContinue} autoFocus>
            I understand, play
          </Button>
        </div>
      </div>
    </div>
  );
}
